import { LiquidPill } from "components/ui/v2";
import { useQuery } from "@apollo/client";
import { CelebrationModalV2 } from "components/ui/v2/explore/CelebrationModalV2";
import { gqlUser } from "gql";
import { Calendar } from "lucide-react";
import moment from "moment";
import useTranslation from "next-translate/useTranslation";
import React from "react";
import UserAvatar from "components/User/UserAvatar";
import { IUser } from "types/User.types";
import SideCard from "../SideCard/SideCard";
import SideCardV2 from "../SideCard/SideCardV2";
import UserItem from "../SideCard/UserItem";
import {
  isCacheValid,
  useExploreWidgetsCacheRefresh,
  useExploreWidgetsStore
} from "stores/explore/useExploreWidgetsStore";

interface AnniversaryWidgetProps {
  readonly variant?: "default" | "v2";
}

const AnniversaryWidget = (props: AnniversaryWidgetProps) => {
  const { variant = "default" } = props;
  const [currentAnniversaries, setCurrentAnniversaries] = React.useState<
    IUser[]
  >([]);

  const [followingAnniversaries, setFollowingAnniversaries] = React.useState<
    IUser[]
  >([]);
  const [celebrationOpen, setCelebrationOpen] = React.useState(false);

  const { t } = useTranslation("celebration");

  const { anniversaries, setAnniversaries, hasHydrated } =
    useExploreWidgetsStore();
  useExploreWidgetsCacheRefresh();
  const hasValidCache = isCacheValid(anniversaries.lastFetched);

  const { loading: anniversariesLoading } = useQuery<{
    usersWithAniversary: IUser[];
  }>(gqlUser.queries.GET_USERS_ANNIVERSARY, {
    fetchPolicy: "network-only",
    skip: !hasHydrated || hasValidCache,
    onCompleted: (data) => {
      if (data?.usersWithAniversary) {
        setAnniversaries(data.usersWithAniversary);
      }
    }
  });

  React.useEffect(() => {
    if (!anniversaries.data) return;

    const current = anniversaries.data.filter(
      (user: IUser) => user.isAnniversary
    );
    const following = anniversaries.data.filter(
      (user: IUser) => !user.isAnniversary
    );

    setCurrentAnniversaries(current);

    const anniversariesThisMonth = following.filter(
      (user: IUser) => moment(user.activeSince).month() === moment().month()
    );

    const anniversaryDayNextMonth = following.filter(
      (user: IUser) =>
        moment(user.activeSince).month() === (moment().month() + 1) % 12
    );

    setFollowingAnniversaries([
      ...anniversariesThisMonth,
      ...anniversaryDayNextMonth
    ]);
  }, [anniversaries.data]);

  const renderItems = (users: IUser[]) => {
    return users.map((user) => (
      <div key={`${user.id}_anniversary_card`}>
        <UserItem user={user} anniversaryCard compact={variant === "v2"} />
      </div>
    ));
  };

  const ActiveSideCard = (
    variant === "v2" ? SideCardV2 : SideCard
  ) as typeof SideCard;

  // No mostrar la card si hoy no hay aniversarios.
  if (!anniversariesLoading && currentAnniversaries.length === 0) {
    return null;
  }

  const initialLoading = anniversariesLoading && !anniversaries.data;

  return (
    <>
      <ActiveSideCard
        title={t("Aniversarios")}
        loading={initialLoading}
        buttonText={currentAnniversaries.length > 1 ? t("Ver todos") : ""}
        onViewMore={() => setCelebrationOpen(true)}
        collapsible={false}
        variant={variant}
        icon={<Calendar size={16} strokeWidth={1} color="#0073FF" />}
        hideViewMore={false}
        headerTag={
          variant === "v2" ? (
            <LiquidPill
              variant="emerald"
              appearance="tinted"
              shadow="neutral"
              size="xxs"
              text={t("Hoy")}
            />
          ) : undefined
        }
      >
        {currentAnniversaries?.length > 0 && (
          <>
            {variant === "v2" ? (
              <div className="mt-1 space-y-2">
                {currentAnniversaries.slice(0, 3).map((anniversaryUser) => {
                  const yearsWorking =
                    moment().year() -
                    moment(anniversaryUser.activeSince).year();

                  return (
                    <div
                      key={anniversaryUser.id}
                      className="flex min-w-0 items-center justify-between gap-3 rounded-2xl px-2 py-1.5"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-semibold leading-tight text-slate-900 dark:text-zinc-100">
                          🎉{" "}
                          {[anniversaryUser.name, anniversaryUser.lastname]
                            .filter(Boolean)
                            .join(" ")}
                        </p>
                        <p className="mt-0.5 truncate text-[11px] text-slate-500 dark:text-zinc-400">
                          {t("Aniversario")} ·{" "}
                          {moment(anniversaryUser.activeSince).format(
                            "DD MMM YYYY"
                          )}
                          {yearsWorking > 0
                            ? ` · ${yearsWorking} ${t(yearsWorking === 1 ? "año" : "años")}`
                            : ""}
                        </p>
                      </div>

                      <div className="shrink-0 rounded-full ring-2 ring-white/90 ring-offset-2 ring-offset-white/60 dark:ring-zinc-900 dark:ring-offset-zinc-800">
                        <UserAvatar user={anniversaryUser} size={54} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="my-2">
                {renderItems(currentAnniversaries.slice(0, 3))}
              </div>
            )}
          </>
        )}

        {/* Intencional: en aniversarios mostramos solo los de hoy dentro de la card. */}
      </ActiveSideCard>
      <CelebrationModalV2
        open={celebrationOpen}
        onClose={() => setCelebrationOpen(false)}
        current={currentAnniversaries}
        following={followingAnniversaries}
        anniversaryCard
      />
    </>
  );
};

export default AnniversaryWidget;
